import React from 'react';
import { Input, Textarea } from '@fluentui/react-components';
import { useSelector } from 'react-redux';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import scopeCheck from './assets/scope-check.svg';
import { resolveWorkspaceScopeAreaOptions } from '../../utils/projectMetadataOptions';
import { engagementAreasFromSuggestion } from './setupSuggestionMapping';
import DocumentContextBanner from './DocumentContextBanner';

export const RECOMMENDED_SCOPE_AREAS = [
    'Applications & Systems',
    'Infrastructure & Cloud',
    'Cybersecurity',
    'Technology Strategy',
    'Data & Analytics',
];

export const DEFAULT_ENGAGEMENT_OBJECTIVE =
    'Assess the scalability and resilience of the technology platform, identify material technology risks, evaluate integration complexity, and identify priority actions following the transaction.';

const ScopeChip = ({ label, selected, onToggle }) => (
    <button
        type="button"
        className={`${styles.scopeChip} ${selected ? styles.scopeChipSelected : ''}`}
        aria-pressed={selected}
        onClick={() => onToggle(label)}
    >
        {selected ? <img src={scopeCheck} alt="" width={12} height={10} className={styles.scopeChipCheck} /> : null}
        {label}
    </button>
);

const CreateProjectStep5 = ({
    formData,
    onChange,
    onContinue,
    onBack,
    continueLoading,
    documentBanner,
    onDismissDocumentBanner,
    onRefreshRecommendations,
    refreshingRecommendations = false,
}) => {
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');
    const [addingCustom, setAddingCustom] = React.useState(false);
    const [customValue, setCustomValue] = React.useState('');

    const activeTenant = React.useMemo(() => {
        const membership = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return membership?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const selectedScopes = Array.isArray(formData?.scope_areas) ? formData.scope_areas : [];
    const selectedSet = React.useMemo(() => new Set(selectedScopes), [selectedScopes]);
    const suggestion = formData?.engagementSuggestion || {};
    const recommendedAreas = React.useMemo(() => {
        const fromSuggestion = engagementAreasFromSuggestion(suggestion).recommended;
        return fromSuggestion.length ? fromSuggestion : RECOMMENDED_SCOPE_AREAS;
    }, [suggestion]);
    const recommendedSet = React.useMemo(() => new Set(recommendedAreas), [recommendedAreas]);
    const suggestedObjective =
        suggestion.engagement_objective_text ||
        (typeof suggestion.engagement_objective === 'string'
            ? suggestion.engagement_objective
            : suggestion.engagement_objective?.text) ||
        DEFAULT_ENGAGEMENT_OBJECTIVE;

    const catalogOptions = React.useMemo(
        () =>
            resolveWorkspaceScopeAreaOptions(activeTenant, {
                includeValues: [...recommendedAreas, ...selectedScopes],
            }),
        [activeTenant, recommendedAreas, selectedScopes],
    );

    const additionalFromSuggestion = React.useMemo(
        () => engagementAreasFromSuggestion(suggestion).additional,
        [suggestion],
    );

    const alsoConsider = React.useMemo(() => {
        if (additionalFromSuggestion.length) {
            return additionalFromSuggestion.filter(value => !recommendedSet.has(value));
        }
        return catalogOptions.map(option => option.value).filter(value => !recommendedSet.has(value));
    }, [additionalFromSuggestion, catalogOptions, recommendedSet]);

    const catalogSet = React.useMemo(() => new Set(catalogOptions.map(option => option.value)), [catalogOptions]);

    const customScopes = selectedScopes.filter(value => !catalogSet.has(value) && !recommendedSet.has(value));

    React.useEffect(() => {
        if (formData?.engagementSeeded) return;
        onChange({
            engagementSeeded: true,
            engagement_objective: formData?.engagement_objective || suggestedObjective,
            scope_areas: Array.isArray(formData?.scope_areas) && formData.scope_areas.length ? formData.scope_areas : recommendedAreas,
        });
        // Seed once when this step is first opened.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.engagementSeeded]);

    const toggleScope = label => {
        const next = selectedSet.has(label) ? selectedScopes.filter(value => value !== label) : [...selectedScopes, label];
        onChange({ scope_areas: next });
    };

    const handleObjectiveChange = e => {
        onChange({ engagement_objective: e.target.value });
    };

    const addCustomScope = event => {
        event?.preventDefault();
        const label = customValue.trim();
        if (!label) return;
        if (!selectedSet.has(label)) {
            onChange({ scope_areas: [...selectedScopes, label] });
        }
        setCustomValue('');
        setAddingCustom(false);
    };

    const objective = formData?.engagement_objective || '';
    const showSuggested = Boolean(suggestedObjective) && objective.trim() === String(suggestedObjective).trim();
    const continueDisabled = !objective.trim() || !selectedScopes.length;

    const contextParts = [formData?.project_type, formData?.customer_stage, formData?.customer_type].filter(Boolean);

    return (
        <NewProjectWizardCard
            step={5}
            wide
            title="Review engagement"
            subtitle="Lens has prepared an initial objective and scope based on the project information provided."
            continueLabel="Confirm & continue"
            continueDisabled={continueDisabled}
            continueLoading={continueLoading}
            onContinue={onContinue}
            onBack={onBack}
            backInline
        >
            {documentBanner ? (
                <DocumentContextBanner
                    variant={documentBanner}
                    onDismiss={onDismissDocumentBanner}
                    onRefresh={onRefreshRecommendations}
                    refreshing={refreshingRecommendations}
                    onNotNow={onDismissDocumentBanner}
                />
            ) : null}
            <div className={styles.fieldBlock}>
                <div className={styles.fieldLabelRow}>
                    <p className={styles.fieldLabel}>
                        Engagement objective <span className={styles.required}>*</span>
                    </p>
                    {showSuggested ? <span className={styles.provenanceSuggested}>Suggested · Lens</span> : null}
                </div>
                <Textarea
                    className={`${styles.textareaInput} ${styles.textareaTall}`}
                    name="engagement_objective"
                    value={objective}
                    onChange={handleObjectiveChange}
                    rows={5}
                    resize="none"
                    required
                />
            </div>

            <div className={styles.scopeSection}>
                <p className={styles.fieldLabel}>
                    Scope areas <span className={styles.required}>*</span>
                </p>
                <p className={styles.scopeGroupLabel}>Recommended for this engagement</p>
                <div className={styles.scopeChipRow}>
                    {recommendedAreas.map(label => (
                        <ScopeChip key={label} label={label} selected={selectedSet.has(label)} onToggle={toggleScope} />
                    ))}
                    {customScopes.map(label => (
                        <ScopeChip key={label} label={label} selected onToggle={toggleScope} />
                    ))}
                </div>

                {alsoConsider.length ? (
                    <>
                        <p className={`${styles.scopeGroupLabel} ${styles.scopeGroupLabelSpaced}`}>Also consider</p>
                        <div className={styles.scopeChipRow}>
                            {alsoConsider.map(label => (
                                <ScopeChip key={label} label={label} selected={selectedSet.has(label)} onToggle={toggleScope} />
                            ))}
                        </div>
                    </>
                ) : null}

                {addingCustom ? (
                    <form className={styles.customScopeRow} onSubmit={addCustomScope}>
                        <Input
                            className={styles.textInput}
                            value={customValue}
                            onChange={e => setCustomValue(e.target.value)}
                            placeholder="Custom scope area"
                            autoFocus
                        />
                        <button type="submit" className={styles.ghostButton}>
                            Add
                        </button>
                        <button
                            type="button"
                            className={styles.ghostButton}
                            onClick={() => {
                                setAddingCustom(false);
                                setCustomValue('');
                            }}
                        >
                            Cancel
                        </button>
                    </form>
                ) : (
                    <button type="button" className={styles.addScopeLink} onClick={() => setAddingCustom(true)}>
                        + Add custom scope area
                    </button>
                )}
            </div>

            {contextParts.length ? (
                <p className={styles.contextUsed}>
                    <span className={styles.contextUsedLabel}>Context used:</span> {contextParts.join(' · ')}
                </p>
            ) : null}
        </NewProjectWizardCard>
    );
};

export default CreateProjectStep5;
